'use strict';
import React, { useEffect, useMemo, useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Typography,
	Box,
	IconButton,
	CircularProgress,
	TextField,
	InputAdornment,
	Alert
} from '@mui/material';
import { Close, OpenInNew, Search } from '@mui/icons-material';
import * as config from '@/config/env.config';
import { Brand } from '@/lenses/uilenses/theme/colors';
import { navigateToIBP } from '@/utils/auth0Token';
import { getProjectItemLabels } from './myprojects/ProjectItem.jsx';

export const IBP_PROJECTS_PAGE_SIZE = 10;

/**
 * Case-insensitive "contains" match on everything a user might know a project by:
 * the Portal project code, the Portal project title, the customer project title and the
 * name IBP itself uses for the project.
 */
export const filterIBPProjects = (projects, searchText) => {
	const needle = (searchText || '').trim().toLowerCase();
	if (!needle) return projects;
	return projects.filter((project) =>
		[project.projectCode, project.projectTitle, project.customerProjectTitle, project.ibpProjectName]
			.some((field) => typeof field === 'string' && field.toLowerCase().includes(needle))
	);
};

// Component for individual project item
const ProjectItem = ({ project, onClose }) => {
	const { primary, secondary } = getProjectItemLabels(project);

	const href = config.API_BIOVIZ + `?projectId=${project.projectId}`;
	
	const handleClick = (e) => {
		e.preventDefault();
		onClose();
		navigateToIBP(href);
	};

	return (
		<ListItem disablePadding>
			<ListItemButton
				onClick={handleClick}
				data-testid="ibp-project-item"
				sx={{
					borderRadius: 2,
					mb: 1,
					'&:hover': {
						background: Brand.metaboliteWhite,
					}
				}}
			>
				<ListItemText
					primary={
						<Typography variant="subtitle1" sx={{ fontWeight: 600, color: Brand.darkModeBlue }}>
							{primary}
						</Typography>
					}
					secondary={
						<Typography variant="body2" sx={{ color: Brand.molecularBlue }}>
							{secondary}
						</Typography>
					}
				/>
			</ListItemButton>
		</ListItem>
	);
};

const openIbpButtonSx = {
	background: Brand.activatedRed,
	'&:hover': {
		background: Brand.insightfulOrange,
	}
};

export const IBPProjectsModal = ({
	open,
	onClose,
	projects = [],
	isLoading = false,
	isError = false,
	isIncomplete = false,
}) => {
	const [searchText, setSearchText] = useState('');
	const [visibleCount, setVisibleCount] = useState(IBP_PROJECTS_PAGE_SIZE);

	// Start every visit to the modal from a clean search and the first page.
	useEffect(() => {
		if (open) {
			setSearchText('');
			setVisibleCount(IBP_PROJECTS_PAGE_SIZE);
		}
	}, [open]);

	const filteredProjects = useMemo(() => filterIBPProjects(projects, searchText), [projects, searchText]);
	const visibleProjects = filteredProjects.slice(0, visibleCount);
	const hasMore = filteredProjects.length > visibleProjects.length;

	const handleSearchChange = (e) => {
		setSearchText(e.target.value);
		setVisibleCount(IBP_PROJECTS_PAGE_SIZE);
	};

	const handleOpenIBP = (e) => {
		if (e) e.preventDefault();
		onClose();
		navigateToIBP(config.API_BIOVIZ);
	};

	const renderBody = () => {
		if (isLoading) {
			return (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
					<CircularProgress sx={{ color: Brand.activatedRed, mb: 2 }} />
					<Typography variant="body1" sx={{ color: Brand.molecularBlue }}>
						Loading projects...
					</Typography>
				</Box>
			);
		}

		// A failed refresh must not hide a list we already have. RTK Query keeps the last
		// successful result when a later refetch errors, so only the empty failure is an
		// empty state; a failure with projects is a banner above that cached list.
		if (isError && projects.length === 0) {
			return (
				<Box sx={{ textAlign: 'center', py: 4 }}>
					<Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }} data-testid="ibp-projects-error">
						The Bioinformatics Platform project list could not be loaded right now. You can still open IBP directly and pick a project there.
					</Alert>
					<Button variant="contained" onClick={handleOpenIBP} sx={openIbpButtonSx}>
						Open IBP <OpenInNew sx={{ ml: 1, fontSize: 18 }} />
					</Button>
				</Box>
			);
		}

		if (projects.length === 0) {
			return (
				<Box sx={{ textAlign: 'center', py: 4 }}>
					<Typography variant="body1" sx={{ color: Brand.molecularBlue, mb: 2 }}>
						No IBP projects available at this time.
					</Typography>
					<Button variant="contained" onClick={handleOpenIBP} sx={openIbpButtonSx}>
						Open IBP <OpenInNew sx={{ ml: 1, fontSize: 18 }} />
					</Button>
				</Box>
			);
		}

		return (
			<>
				<Typography variant="body2" sx={{ color: Brand.molecularBlue, mb: 2 }}>
					Select a project to access IBP, or open IBP directly to explore all available data.
				</Typography>

				{isError && (
					<Alert severity="warning" sx={{ mb: 2 }} data-testid="ibp-projects-error">
						The project list could not be refreshed. Showing the last list that loaded. If yours is missing, open IBP directly.
					</Alert>
				)}

				{isIncomplete && (
					<Alert severity="info" sx={{ mb: 2 }} data-testid="ibp-projects-incomplete">
						Some projects could not be loaded from the Bioinformatics Platform. If yours is missing, open IBP directly.
					</Alert>
				)}

				<TextField
					fullWidth
					size="small"
					placeholder="Search by project code or title..."
					value={searchText}
					onChange={handleSearchChange}
					inputProps={{ 'aria-label': 'Search IBP projects', 'data-testid': 'ibp-projects-search' }}
					sx={{ mb: 2 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search sx={{ color: Brand.molecularBlue }} />
							</InputAdornment>
						),
					}}
				/>

				<Typography variant="caption" sx={{ color: Brand.molecularBlue, display: 'block', mb: 1 }} data-testid="ibp-projects-count">
					{searchText.trim()
						? `${filteredProjects.length} of ${projects.length} projects match`
						: `${projects.length} projects`}
				</Typography>

				{filteredProjects.length > 0 ? (
					<Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
						<List sx={{ padding: 0 }}>
							{visibleProjects.map((project) => (
								<ProjectItem key={project.projectId} project={project} onClose={onClose} />
							))}
						</List>
						{hasMore && (
							<Box sx={{ textAlign: 'center', mt: 1 }}>
								<Button
									variant="text"
									onClick={() => setVisibleCount((count) => count + IBP_PROJECTS_PAGE_SIZE)}
									sx={{ color: Brand.molecularBlue, fontWeight: 600 }}
									data-testid="ibp-projects-show-more"
								>
									Show more ({filteredProjects.length - visibleProjects.length} remaining)
								</Button>
							</Box>
						)}
					</Box>
				) : (
					<Box sx={{ textAlign: 'center', py: 4 }}>
						<Typography variant="body1" sx={{ color: Brand.molecularBlue, mb: 2 }}>
							No projects match your search.
						</Typography>
						<Button
							variant="outlined"
							onClick={() => setSearchText('')}
							sx={{
								borderColor: Brand.molecularBlue,
								color: Brand.molecularBlue,
								'&:hover': {
									borderColor: Brand.activatedRed,
									background: `${Brand.activatedRed}10`,
								}
							}}
						>
							Clear Search
						</Button>
					</Box>
				)}
			</>
		);
	};

	return (
		<Dialog 
			open={open} 
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 3,
					boxShadow: '0 8px 32px rgba(0, 29, 56, 0.2)',
				}
			}}
		>
			<DialogTitle sx={{ 
				background: `linear-gradient(135deg, ${Brand.activatedRed} 0%, ${Brand.molecularBlue} 100%)`,
				color: Brand.pureWhite,
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				padding: '20px 24px',
				fontWeight: 700
			}}>
				Integrated Bioinformatics Platform
				<IconButton
					onClick={onClose}
					sx={{ color: Brand.pureWhite }}
					size="small"
				>
					<Close />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ padding: '24px' }}>
				{renderBody()}
			</DialogContent>
			<DialogActions sx={{ padding: '16px 24px', borderTop: `1px solid ${Brand.precisionGrey}` }}>
				<Button onClick={onClose} sx={{ color: Brand.molecularBlue }}>
					Cancel
				</Button>
				{!isLoading && projects.length > 0 && (
					<Button
						onClick={handleOpenIBP}
						variant="contained"
						sx={openIbpButtonSx}
					>
						Open IBP Directly <OpenInNew sx={{ ml: 1, fontSize: 18 }} />
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default IBPProjectsModal;
